/**
 * Popsigns Universal Analytics Event Bridge
 *
 * Listens to existing internal events (StateManager, CustomEvents) and fans out
 * to GA4 (gtag), FullStory (FS), and Microsoft Clarity (clarity).
 *
 * Pattern: Same as klaviyo-cart-tracking.js — standalone IIFE module that
 * subscribes to internal events and bridges to external platforms.
 *
 * Version: 1.12.0
 *
 * v1.12.0 (2026-04-28): Fix LEAK-B-09 auto-fire bugs. Three GA4 events
 * (design_radio_changed, design_method_selected, file_upload_started) were
 * firing on every product page load via StateManager init paths. Fix:
 *   - design_radio_changed: rely on DOM `change` event (line ~476) only;
 *     remove StateManager `project:changed` subscription (auto-fires on
 *     setProject during default radio auto-selection)
 *   - design_method_selected: switch from StateManager `design:method:changed`
 *     to DOM `design:method:user-changed` (dispatched from design-method-toggle.js
 *     only on real user radio toggle)
 *   - file_upload_started: switch from StateManager `upload:mode:changed`
 *     to DOM `upload:mode:user-changed` (dispatched from custom-file-uploader.js
 *     only inside handleModeChange, which is only called from user clicks)
 */
(() => {
  'use strict';

  const LOG_PREFIX = '[PopsignsAnalytics]';
  const PLATFORM_TIMEOUT_MS = 8000;
  const PLATFORM_POLL_MS = 500;

  const log = (...args) => window.PITCHPRINT_DEBUG && console.log(LOG_PREFIX, ...args);

  // --- Visible debug overlay (only when ?analytics_debug=1 is in URL) ---
  // Persists events to sessionStorage so they survive page reloads (e.g., after ATC)
  const DEBUG_STORAGE_KEY = 'popsigns_analytics_debug_log';
  const _hasUrlParam = new URLSearchParams(window.location.search).has('analytics_debug');
  const _hasSession = !!sessionStorage.getItem(DEBUG_STORAGE_KEY);
  const SHOW_OVERLAY = window.PITCHPRINT_DEBUG && (_hasUrlParam || _hasSession);
  let debugOverlay = null;

  function getStoredEvents() {
    try { return JSON.parse(sessionStorage.getItem(DEBUG_STORAGE_KEY) || '[]'); } catch { return []; }
  }

  function initOverlay() {
    debugOverlay = document.createElement('div');
    debugOverlay.id = 'popsigns-analytics-debug';
    debugOverlay.style.cssText = 'position:fixed;bottom:10px;right:10px;width:380px;max-height:300px;overflow-y:auto;background:rgba(0,0,0,0.85);color:#0f0;font:11px/1.4 monospace;padding:8px;border-radius:6px;z-index:999999;pointer-events:auto;';
    debugOverlay.innerHTML = '<div style="color:#ff0;margin-bottom:4px;font-weight:bold;display:flex;justify-content:space-between;">Analytics Debug v1.7.0<span id="analytics-debug-clear" style="cursor:pointer;color:#f66;">Clear</span></div>';
    document.body.appendChild(debugOverlay);
    debugOverlay.querySelector('#analytics-debug-clear').addEventListener('click', () => {
      sessionStorage.removeItem(DEBUG_STORAGE_KEY);
      debugOverlay.querySelectorAll('.debug-entry').forEach(el => el.remove());
    });
    // Restore events from previous page
    const stored = getStoredEvents();
    stored.forEach(text => appendEntry(text, true));
  }

  function appendEntry(text, isRestored) {
    if (!debugOverlay) return;
    const entry = document.createElement('div');
    entry.className = 'debug-entry';
    entry.style.cssText = 'border-bottom:1px solid #333;padding:2px 0;' + (isRestored ? 'color:#0a0;opacity:0.7;' : '');
    entry.textContent = text;
    debugOverlay.appendChild(entry);
    debugOverlay.scrollTop = debugOverlay.scrollHeight;
  }

  function showDebugEvent(text) {
    if (!SHOW_OVERLAY) return;
    if (!debugOverlay) initOverlay();
    const line = new Date().toLocaleTimeString() + ' ' + text;
    appendEntry(line, false);
    // Persist to sessionStorage (keep last 50)
    const stored = getStoredEvents();
    stored.push(line);
    if (stored.length > 50) stored.splice(0, stored.length - 50);
    try { sessionStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(stored)); } catch {}
  }

  // --- Platform config from Liquid ---
  const config = window.__popsignsAnalytics;
  if (!config) {
    log('Config not found (window.__popsignsAnalytics), skipping');
    return;
  }

  // --- Platform readiness ---

  const platforms = {
    gtag: { ready: false, queue: [] },
    fs: { ready: false, queue: [] },
    clarity: { ready: false, queue: [] }
  };

  function waitForPlatform(name, check) {
    return new Promise((resolve) => {
      if (check()) {
        platforms[name].ready = true;
        resolve(true);
        return;
      }

      let attempts = 0;
      const maxAttempts = PLATFORM_TIMEOUT_MS / PLATFORM_POLL_MS;
      const interval = setInterval(() => {
        attempts++;
        if (check()) {
          clearInterval(interval);
          platforms[name].ready = true;
          // Flush queued events
          platforms[name].queue.forEach(fn => fn());
          platforms[name].queue = [];
          resolve(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          log(name + ' not available after ' + PLATFORM_TIMEOUT_MS + 'ms');
          resolve(false);
        }
      }, PLATFORM_POLL_MS);
    });
  }

  // --- Event dispatch to platforms ---

  function sendToGA4(eventName, properties) {
    const send = () => {
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, properties);
        }
        // No dataLayer.push fallback. The previous fallback spread event properties
        // as top-level dataLayer keys, which caused GA4 to interpret values like
        // 'mini_cart', 'fetch_intercept', 'start_designing_button' as session traffic
        // sources — attributing ~45% of revenue to "Unassigned". Shopify's native GA4
        // pixel uses its own event bus, not the traditional GTM dataLayer, so pushing
        // to dataLayer without gtag() likely wasn't reaching GA4 anyway.
        log('GA4 →', eventName, properties);
      } catch (err) {
        log('GA4 error:', err);
      }
    };

    if (platforms.gtag.ready) {
      send();
    } else {
      platforms.gtag.queue.push(send);
    }
  }

  function sendToFullStory(eventName, properties) {
    const send = () => {
      try {
        window.FS('trackEvent', { name: eventName, properties: properties });
        log('FS →', eventName, properties);
      } catch (err) {
        log('FS error:', err);
      }
    };

    if (platforms.fs.ready) {
      send();
    } else {
      platforms.fs.queue.push(send);
    }
  }

  function sendToClarity(eventName, properties) {
    const send = () => {
      try {
        // Clarity custom tags: set a tag with the event name
        window.clarity('set', eventName, JSON.stringify(properties));
        log('Clarity →', eventName, properties);
      } catch (err) {
        log('Clarity error:', err);
      }
    };

    if (platforms.clarity.ready) {
      send();
    } else {
      platforms.clarity.queue.push(send);
    }
  }

  /**
   * Track an event across all platforms.
   * @param {string} eventName - GA4-style event name (snake_case)
   * @param {Object} properties - Event properties
   */
  function track(eventName, properties) {
    const enriched = {
      ...properties,
      page_template: config.template,
      timestamp: Date.now()
    };

    log('Track:', eventName, enriched);
    showDebugEvent('TRACK: ' + eventName);

    sendToGA4(eventName, enriched);
    sendToFullStory(eventName, enriched);
    sendToClarity(eventName, enriched);
  }

  // Expose for direct use from other scripts if needed
  window.PopsignsAnalytics = { track };

  // --- Event subscriptions ---

  function subscribeToStateManager() {
    const sm = window.PitchPrintStateManager;
    if (!sm || !sm.on) {
      log('StateManager not available, skipping SM subscriptions');
      return;
    }

    // (LEAK-B-09 fix) Removed `project:changed` subscription: it auto-fired
    // on every product page load when design-radio-selector-v314.js auto-checks
    // a default radio at init, calling setProject which emits project:changed
    // even though no user interaction occurred. The DOM `change` listener
    // further down (around line ~480) catches real radio clicks correctly,
    // since browsers do NOT fire `change` on programmatic .checked = true.

    // PitchPrint editor ready
    sm.on('system:ready', () => {
      track('pitchprint_editor_opened', {
        product_id: config.productId,
        product_title: config.productTitle,
        design_id: sm.getDesignId() || null
      });
    });

    // File upload completed
    sm.on('upload:completed', (e) => {
      const data = e.detail || {};
      track('file_upload_completed', {
        product_id: config.productId,
        product_title: config.productTitle,
        sides: data.sides || null,
        shape: data.shape || null
      });
    });

    // (LEAK-B-09 fix) `upload:mode:changed` from StateManager fires from
    // every emitStateChange() call inside the file uploader (proof checkbox,
    // file processed, file removed, size changed, etc.) regardless of whether
    // the user actually toggled mode. We now listen to a DOM custom event
    // dispatched only from handleModeChange() in custom-file-uploader.js,
    // which is only called from the user's radio change handler.
    document.addEventListener('upload:mode:user-changed', (e) => {
      const mode = e.detail;
      if (mode) {
        track('file_upload_started', {
          product_id: config.productId,
          product_title: config.productTitle,
          upload_mode: mode
        });
      }
    });

    // Design saved / project restored
    sm.on('project:restored', (e) => {
      const data = e.detail || {};
      track('design_saved', {
        product_id: config.productId,
        product_title: config.productTitle,
        project_id: data.projectId || null
      });
    });

    // (LEAK-B-09 fix) `design:method:changed` from StateManager auto-fires on
    // every fresh page load because design-method-toggle.js init at line 49-51
    // calls setDesignMethod('pitchprint') when no localStorage method is set.
    // We now listen to a DOM custom event dispatched only from the user-radio
    // change handler in design-method-toggle.js (not from init).
    document.addEventListener('design:method:user-changed', (e) => {
      const method = e.detail;
      track('design_method_selected', {
        product_id: config.productId,
        product_title: config.productTitle,
        method: method
      });
    });

    // Design path changed (quick vs custom toggle)
    sm.on('design:path:changed', (e) => {
      const path = e.detail;
      track('design_path_selected', {
        product_id: config.productId,
        product_title: config.productTitle,
        path: path
      });
    });

    // Variant changed (hardware-only detection)
    sm.on('variant:hardware-only:changed', (e) => {
      const data = e.detail || {};
      track('variant_changed', {
        product_id: config.productId,
        product_title: config.productTitle,
        is_hardware_only: data.isHardwareOnly || false
      });
    });

    // Quick design step changed
    sm.on('quickdesign:step:changed', (e) => {
      const data = e.detail || {};
      track('quick_design_step', {
        product_id: config.productId,
        product_title: config.productTitle,
        step: data.step || null,
        front_selected: data.frontSelected || false,
        back_selected: data.backSelected || false
      });
    });

    // Error logged
    sm.on('error:logged', (e) => {
      const data = e.detail || {};
      track('pitchprint_error', {
        product_id: config.productId,
        product_title: config.productTitle,
        error_context: data.context || 'unknown',
        error_message: data.message || ''
      });
    });

    log('Subscribed to StateManager events');
  }

  function subscribeToATCEvents() {
    // Listen for ATC state machine transitions
    document.addEventListener('unified-addtocart:state-changed', (e) => {
      log('ATC state transition:', e.detail?.oldState, '→', e.detail?.newState);
      showDebugEvent('ATC: ' + (e.detail?.oldState || '?') + ' → ' + (e.detail?.newState || '?'));
      const detail = e.detail || {};
      const newState = detail.newState;
      const oldState = detail.oldState;

      // Track specific high-value transitions
      if (newState === 'processing') {
        track('add_to_cart_attempted', {
          product_id: config.productId,
          product_title: config.productTitle,
          from_state: oldState
        });
      } else if (newState === 'success') {
        track('add_to_cart_success', {
          product_id: config.productId,
          product_title: config.productTitle,
          from_state: oldState
        });
      } else if (newState === 'error') {
        track('add_to_cart_error', {
          product_id: config.productId,
          product_title: config.productTitle,
          from_state: oldState,
          error_state: newState
        });
      }
    });

    log('Subscribed to ATC state-changed events');
  }

  function subscribeToCartEvents() {
    // Listen for cart:updated CustomEvent
    document.addEventListener('cart:updated', (e) => {
      const detail = e.detail || {};
      if (detail.product_id) {
        track('cart_item_added', {
          product_id: detail.product_id,
          product_title: detail.product_title || '',
          variant_id: detail.variant_id || null
        });
      }
    });

    // Intercept fetch calls to /cart/add.js to catch ALL add-to-cart paths
    // (PitchPrint editor submits directly, bypassing the unified ATC state machine)
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      const isCartAdd = url.includes('/cart/add');

      if (isCartAdd) {
        track('add_to_cart_attempted', {
          product_id: config.productId,
          product_title: config.productTitle,
          interaction_source: 'fetch_intercept'
        });
      }

      return originalFetch.apply(this, args).then(response => {
        if (isCartAdd) {
          if (response.ok) {
            track('add_to_cart_success', {
              product_id: config.productId,
              product_title: config.productTitle,
              interaction_source: 'fetch_intercept'
            });
          } else {
            track('add_to_cart_error', {
              product_id: config.productId,
              product_title: config.productTitle,
              interaction_source: 'fetch_intercept',
              status: response.status
            });
          }
        }
        return response;
      }).catch(err => {
        if (isCartAdd) {
          track('add_to_cart_error', {
            product_id: config.productId,
            product_title: config.productTitle,
            interaction_source: 'fetch_intercept',
            error: err.message
          });
        }
        throw err;
      });
    };

    // Also intercept XMLHttpRequest for legacy code paths
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._analyticsUrl = url;
      return originalXHROpen.call(this, method, url, ...rest);
    };
    const originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(...args) {
      if (this._analyticsUrl && this._analyticsUrl.includes('/cart/add')) {
        track('add_to_cart_attempted', {
          product_id: config.productId,
          product_title: config.productTitle,
          interaction_source: 'xhr_intercept'
        });
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            track('add_to_cart_success', {
              product_id: config.productId,
              product_title: config.productTitle,
              interaction_source: 'xhr_intercept'
            });
          } else {
            track('add_to_cart_error', {
              product_id: config.productId,
              product_title: config.productTitle,
              interaction_source: 'xhr_intercept',
              status: this.status
            });
          }
        });
      }
      return originalXHRSend.apply(this, args);
    };

    log('Subscribed to cart events (+ fetch/XHR intercept)');
  }

  // --- DOM-level event listeners ---

  function subscribeToDOMEvents() {
    if (config.template !== 'product') return;

    // Debug: log ALL click and change events to visible overlay + console
    if (SHOW_OVERLAY) {
      document.addEventListener('click', (e) => {
        const tag = e.target.tagName;
        const cls = (typeof e.target.className === 'string') ? e.target.className.substring(0, 40) : '';
        const closest = e.target.closest('.card-p-style, .step2-template-thumb, .design-radio-selector__option, label');
        if (closest) {
          const msg = 'CLICK: ' + tag + ' → ' + (closest.className || '').toString().substring(0, 40);
          showDebugEvent(msg);
          console.log(LOG_PREFIX, msg);
        }
      }, true);

      document.addEventListener('change', (e) => {
        const tag = e.target.tagName;
        const type = e.target.type || '';
        const name = e.target.name || '';
        const msg = 'CHANGE: ' + tag + ' type=' + type + ' name=' + name;
        showDebugEvent(msg);
        console.log(LOG_PREFIX, msg);
      }, true);
    }

    // Design Radio Selector — radio input change (main design picker)
    document.addEventListener('change', (e) => {
      const radio = e.target;
      log('Change event received:', radio.tagName, 'type=' + radio.type, 'name=' + radio.name);
      if (radio.type !== 'radio') return;
      if (radio.name !== 'design_template_front' && radio.name !== 'design_template_back') return;

      const side = radio.name.includes('back') ? 'back' : 'front';
      track('design_radio_changed', {
        product_id: config.productId,
        product_title: config.productTitle,
        design_name: radio.dataset.title || '',
        design_side: side,
        collection_page: radio.dataset.collectionPage || null
      });
    });

    // PitchPrint editor launched — user intentionally opened the editor.
    // Covers two paths:
    // 1. Sticky ATC click (need_design state) → dispatches 'pitchprint:editor:launched' custom event
    // 2. Direct click on #pp_main_btn_sec button ("Start Designing") — e.isTrusted guards against
    //    programmatic clicks from triggerCustomizationPrompt() to prevent double-counting

    document.addEventListener('pitchprint:editor:launched', (e) => {
      track('pitchprint_editor_launched', {
        product_id: config.productId,
        product_title: config.productTitle,
        interaction_source: (e.detail && e.detail.source) || 'unknown'
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.isTrusted) return; // ignore programmatic clicks (e.g. from triggerCustomizationPrompt)
      const ppBtn = e.target.closest('#pp_main_btn_sec button, #pp_main_btn_sec a');
      if (!ppBtn) return;
      track('pitchprint_editor_launched', {
        product_id: config.productId,
        product_title: config.productTitle,
        interaction_source: 'start_designing_button'
      });
    });

    // Quick Design thumbnail clicks (design-thumbnails.liquid)
    document.addEventListener('click', (e) => {
      const thumb = e.target.closest('.step2-template-thumb');
      if (!thumb) return;

      const idx = thumb.dataset.designIndex;
      const imgTag = thumb.querySelector('img');
      const name = imgTag ? imgTag.alt : 'Design ' + (parseInt(idx) + 1);

      track('design_thumbnail_click', {
        product_id: config.productId,
        product_title: config.productTitle,
        design_index: idx,
        design_name: name
      });
    });

    // Variant card clicks (Choose Sides, Choose Shape, Choose Size)
    // Cards use class .card-p-style with data-option-name and data-value attributes
    document.addEventListener('click', (e) => {
      const card = e.target.closest('.card-p-style');
      if (!card) return;

      log('Card click detected:', card.dataset.optionName, card.dataset.value);
      track('variant_option_clicked', {
        product_id: config.productId,
        product_title: config.productTitle,
        option_name: card.dataset.optionName || card.dataset.optionSlug || '',
        option_value: card.dataset.value || card.textContent.trim().substring(0, 50)
      });
    });

    // Shopify native variant change — debounced to avoid duplicates
    // (multiple hidden inputs and selects fire change events simultaneously)
    // Gate on real user interaction to prevent page-load initialization from
    // firing variant_changed (card-variants applies popular/URL variants on load,
    // which programmatically dispatches change events on hidden selects/inputs)
    let userHasInteractedWithPage = false;
    document.addEventListener('pointerdown', () => { userHasInteractedWithPage = true; }, { once: true, capture: true });

    let variantChangeTimer = null;
    document.addEventListener('change', (e) => {
      if (e.target.name === 'id' || e.target.closest('variant-selects')) {
        if (!userHasInteractedWithPage) {
          log('Suppressing variant_changed — no user interaction yet (page-load init)');
          return;
        }
        if (variantChangeTimer) clearTimeout(variantChangeTimer);
        variantChangeTimer = setTimeout(() => {
          variantChangeTimer = null;
          const variantInput = document.querySelector('input[name="id"]');
          track('variant_changed', {
            product_id: config.productId,
            product_title: config.productTitle,
            variant_id: variantInput ? variantInput.value : null
          });
        }, 150);
      }
    });

    log('Subscribed to DOM events (radios, thumbnails, variant cards)');
  }

  // --- Product page view tracking ---

  function trackProductPageView() {
    if (config.template !== 'product') return;
    if (!config.productId) return;

    track('page_view_product', {
      product_id: config.productId,
      product_title: config.productTitle,
      product_type: config.productType,
      collection: config.collection
    });
  }

  // --- Checkout tracking (runs on all pages — cart page + mini-cart drawer) ---

  function subscribeToCheckoutEvents() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="checkout"], a[href*="/checkout"]');
      if (!btn) return;

      track('begin_checkout', {
        cart_item_count: config.cartItemCount,
        cart_total: config.cartTotal,
        interaction_source: btn.classList.contains('cart__checkout-button') ? 'cart_page' : 'mini_cart'
      });
    });

    // Also intercept form submissions to /checkout
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/checkout')) {
        track('begin_checkout', {
          cart_item_count: config.cartItemCount,
          cart_total: config.cartTotal,
          interaction_source: 'form_submit'
        });
      }
    });

    log('Subscribed to checkout events');
  }

  // --- Initialization ---

  function init() {
    log('Initializing...');

    // Start platform detection in parallel
    // GA4: only accept gtag as a function — dataLayer.push fallback was removed because
    // spreading event properties as top-level dataLayer keys polluted session attribution
    waitForPlatform('gtag', () => typeof window.gtag === 'function').then(ready => {
      log('GA4 platform detection result:', ready, '| gtag=' + (typeof window.gtag));
    });
    waitForPlatform('fs', () => typeof window.FS === 'function');
    waitForPlatform('clarity', () => typeof window.clarity === 'function');

    // Track product page view
    trackProductPageView();

    // Subscribe to internal events
    subscribeToStateManager();
    subscribeToATCEvents();
    subscribeToCartEvents();
    subscribeToDOMEvents();
    subscribeToCheckoutEvents();

    log('Initialized — listening for events');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
